import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/data/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    }
    if (mobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen]);

  function handleCatClick(url: string) {
    setMobileOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <>
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200 shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg font-heading font-bold text-foreground-950">Superbuy</span>
            <span className="text-lg font-heading font-light text-foreground-500 hidden sm:inline">Spreadsheet</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-6">
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
              Shop All
            </a>
            <Link to="/article/superbuy-spreadsheet-ultimate-guide" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
              Guides
            </Link>
            <Link to="/article/superbuy-spreadsheet-for-sneakerheads" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
              Sneaker Guide
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center px-5 py-2 bg-foreground-950 text-background-50 font-medium text-sm rounded-full hover:bg-foreground-800 transition-colors duration-200 cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <i className="ri-close-line text-2xl text-foreground-800" />
            ) : (
              <i className="ri-menu-line text-2xl text-foreground-800" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div ref={panelRef} className="absolute top-0 right-0 h-full w-[85vw] max-w-[380px] bg-background-50 shadow-2xl overflow-y-auto animate-slide-in">
            <div className="p-6 pt-20">
              <div className="flex flex-col gap-1 mb-8">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground-400 mb-3">Browse Categories</p>
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCatClick(cat.url)}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 cursor-pointer flex items-center justify-between group"
                  >
                    <span>{cat.name}</span>
                    <i className="ri-external-link-line text-xs text-foreground-300 group-hover:text-foreground-500 transition-colors" />
                  </button>
                ))}
              </div>

              <div className="border-t border-background-200 pt-6 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground-400 mb-3">Quick Links</p>
                <Link
                  to="/article/superbuy-spreadsheet-ultimate-guide"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                >
                  <i className="ri-book-open-line mr-2 text-foreground-400" />
                  Ultimate Guide
                </Link>
                <Link
                  to="/article/superbuy-spreadsheet-for-sneakerheads"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                >
                  <i className="ri-footprint-line mr-2 text-foreground-400" />
                  Sneakerheads Guide
                </Link>
                <Link
                  to="/article/track-qc-photos-superbuy-sheet"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                >
                  <i className="ri-image-line mr-2 text-foreground-400" />
                  QC Photos Guide
                </Link>
                <Link
                  to="/article/superbuy-spreadsheet-pricing-budget-guide"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                >
                  <i className="ri-money-dollar-circle-line mr-2 text-foreground-400" />
                  Budget Guide
                </Link>
              </div>

              <div className="border-t border-background-200 pt-6 mt-6 space-y-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full text-center px-5 py-3 bg-foreground-950 text-background-50 font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors duration-200"
                >
                  Superbuy Sheet
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full text-center px-5 py-3 border border-foreground-300 text-foreground-800 font-medium text-sm rounded-full hover:bg-foreground-50 transition-colors duration-200"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}